import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { submitConstructionWaitlist } from '../services/WaitlistService';
import {
  CONSTRUCTION_THREAD_EVENT,
   ConstructionThreadDetail,
} from '../utils/constructionNotice';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOCAL_STORAGE_KEY = 'construction_waitlist_state';

export interface ConstructionWaitlistController {
  visible: boolean;
  promptPreview: string;
  responseText: string;
  firstName: string;
  setFirstName: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  linkedin: string;
  setLinkedin: (value: string) => void;
  xHandle: string;
  setXHandle: (value: string) => void;
  submitting: boolean;
  error: string;
  success: boolean;
  hasPersistedSuccess: boolean;
  confirmationTimestamp: number | null;
  reminderDue: boolean;
  close: () => void;
  submit: () => Promise<boolean>;
  beginAnotherEntry: () => void;
}

export function useConstructionWaitlist(): ConstructionWaitlistController {
  const [visible, setVisible] = useState(false);
  const [promptPreview, setPromptPreview] = useState('');
  const [responseText, setResponseText] = useState('');
  const [source, setSource] = useState<string | undefined>(undefined);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [xHandle, setXHandle] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [hasPersistedSuccess, setHasPersistedSuccess] = useState(false);
  const [confirmationTimestamp, setConfirmationTimestamp] = useState<number | null>(null);
  const lastEventRef = useRef<number>(0);
  const persistedSuccessRef = useRef<boolean>(false);

  // Hydrate persisted success state so confirmation stays in history locally
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw) as {
        success?: boolean;
        responseText?: string;
        email?: string;
        firstName?: string;
        linkedin?: string;
        xHandle?: string;
        ts?: number;
      };
      if (data?.success) {
        setSuccess(true);
        setHasPersistedSuccess(true);
        setVisible(true);
        persistedSuccessRef.current = true;
        if (data.ts) setConfirmationTimestamp(data.ts);
        if (data.responseText) setResponseText(data.responseText);
        if (data.email) setEmail(data.email);
        if (data.firstName) setFirstName(data.firstName);
        if (data.linkedin) setLinkedin(data.linkedin);
        if (data.xHandle) setXHandle(data.xHandle);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const listener = ((event: Event) => {
      // If we already have a successful, persisted confirmation, do not reset state on subsequent thread events.
      if (persistedSuccessRef.current || success) {
        return;
      }
      const custom = event as CustomEvent<ConstructionThreadDetail>;
      const detail = custom.detail || { response: '' };
      const now = Date.now();
      lastEventRef.current = now;
      setPromptPreview(detail.prompt || '');
      setResponseText(detail.response || '');
      setSource(detail.source);
      setFirstName('');
      setEmail('');
      setLinkedin('');
      setXHandle('');
      setError('');
      setSuccess(false);
      setHasPersistedSuccess(false);
      setVisible(true);
    }) as EventListener;

    window.addEventListener(CONSTRUCTION_THREAD_EVENT, listener);
    return () => {
      window.removeEventListener(CONSTRUCTION_THREAD_EVENT, listener);
    };
  }, []);

  const close = useCallback(() => {
    // Do not allow closing when success is shown; keep it persistent in history
    if (success) return;
    setVisible(false);
  }, [success]);

  const submit = useCallback(async () => {
    if (submitting) return false;
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !EMAIL_PATTERN.test(normalizedEmail)) {
      setError('enter a valid email address');
      return false;
    }

    setSubmitting(true);
    setError('');
    try {
      await submitConstructionWaitlist({
        email: normalizedEmail,
        firstName: firstName.trim() || undefined,
        messageText: promptPreview,
        source: source || 'prompt_bar',
        linkedinUrl: linkedin.trim() || undefined,
        xHandle: xHandle.trim() || undefined,
      });
      const ts = Date.now();
      setSuccess(true);
      setHasPersistedSuccess(true);
      setConfirmationTimestamp(ts);
      persistedSuccessRef.current = true;
      // Persist confirmation locally so it stays visible
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
          success: true,
          responseText,
          email: normalizedEmail,
          firstName: firstName.trim() || undefined,
          linkedin: linkedin.trim() || undefined,
          xHandle: xHandle.trim() || undefined,
          ts,
        }));
      } catch {}
      return true;
    } catch (err: any) {
      const code = String(err?.code || '').toUpperCase();
      let message = String(err?.message || '').trim();
      if (!message) {
        message = 'unable to save your wait list request right now';
      }
      if (code === 'HTTP_404') {
        message = "wait list service is offline. deploy submitPublicConstructionWaitlist via 'firebase deploy --only functions:submitPublicConstructionWaitlist' and try again.";
      } else if (code === 'HTTP_500') {
        message = 'the wait list service returned an internal error. check Cloud Function logs for submitPublicConstructionWaitlist to resolve and then retry.';
      }
      setError(message);
      setSuccess(false);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [email, firstName, linkedin, promptPreview, responseText, source, submitting, xHandle]);

  const beginAnotherEntry = useCallback(() => {
    // Keep the original confirmation visible but prepare a fresh form
    setFirstName('');
    setEmail('');
    setLinkedin('');
    setXHandle('');
    setError('');
    setSuccess(false);
    setVisible(true);
  }, []);

  const reminderDue = !!(hasPersistedSuccess && confirmationTimestamp && (Date.now() - confirmationTimestamp) > 24 * 60 * 60 * 1000);

  // ONE OBJECT PER CHANGE OF A FIELD (2026-09-24). The chat builds its message rows
  // inside a memo, and the waitlist bubble is one of those rows: it names this
  // controller in its inputs, so the object must change when (and only when) one of
  // its fields does. A new object every render would rebuild every row on every
  // keystroke in the composer; the same object after a field changed would leave the
  // bubble's form showing old words (a controlled field reverts to them).
  return useMemo(() => ({
    visible,
    promptPreview,
    responseText,
    firstName,
    setFirstName,
    email,
    setEmail,
    linkedin,
    setLinkedin,
    xHandle,
    setXHandle,
    submitting,
    error,
    success,
    hasPersistedSuccess,
    confirmationTimestamp,
    reminderDue,
    close,
    submit,
    beginAnotherEntry,
  }), [
    visible, promptPreview, responseText, firstName, email, linkedin, xHandle, submitting, error, success,
    hasPersistedSuccess, confirmationTimestamp, reminderDue, close, submit, beginAnotherEntry,
  ]);
}

